/**
 * Devices view (#999 Story 3.5 / 3.7).
 *
 * Inventory-first replacement for the Story 3.3 chip-filter Location view:
 * all locations as cards (creation order), each with step-mirrored tables per
 * equipment type. Story 3.7: shared reorder + tap opens unit editor.
 */

import React from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { LocationCard } from './LocationCard';
import { useRequestScope } from '../../../contexts/RequestScopeContext';
import {
  useLocationViewData,
  type DevicesUnitOverrides,
} from '../../../hooks/useLocationViewData';
import { colors, spacing } from '../../../theme';
import { Layers } from '../../icons';
import { StepShell } from '../../StepShell';

import type {
  DeviceSourceUnit,
  DeviceStepId,
} from '../../../utils/deviceRowMapper';
import type { DevicesViewModel } from '../../../utils/devicesViewModel';

const EMPTY_STEP_IDS: readonly string[] = [];

export interface DevicesViewProps {
  requestId: string;
  enabledStepIds?: readonly string[];
  unitOverrides?: DevicesUnitOverrides;
  onUnitPress?: (stepId: DeviceStepId, unit: DeviceSourceUnit) => void;
  onReorderUnits?: (
    stepId: DeviceStepId,
    visibleUnits: readonly DeviceSourceUnit[],
    fromIndex: number,
    toIndex: number,
  ) => void;
  canReorderStep?: (stepId: DeviceStepId) => boolean;
  testID?: string;
}

interface DevicesViewBodyProps {
  viewModel: DevicesViewModel;
  isLoading: boolean;
  isError?: boolean;
  onUnitPress?: (stepId: DeviceStepId, unit: DeviceSourceUnit) => void;
  onReorderUnits?: (
    stepId: DeviceStepId,
    visibleUnits: readonly DeviceSourceUnit[],
    fromIndex: number,
    toIndex: number,
  ) => void;
  canReorderStep?: (stepId: DeviceStepId) => boolean;
  resolveLocationTagName?: (
    locationTagId: number | null | undefined,
  ) => string | null;
  testID?: string;
}

export const DevicesViewBody: React.FC<DevicesViewBodyProps> = ({
  viewModel,
  isLoading,
  isError = false,
  onUnitPress,
  onReorderUnits,
  canReorderStep,
  resolveLocationTagName,
  testID = 'pd-devices-view',
}) => {
  const { cards, summary } = viewModel;
  const showInventoryEmpty = summary.totalDevices === 0;

  return (
    <View testID={testID} style={styles.container}>
      <StepShell title="Devices" testID={`${testID}-title`}>
        {isLoading ? (
          <View
            style={styles.stateBox}
            testID={`${testID}-loading`}
            accessibilityRole="progressbar"
            accessibilityLabel="Loading devices"
          >
            <ActivityIndicator color={colors.primary} />
            <Text style={styles.emptyText}>Loading devices…</Text>
          </View>
        ) : isError ? (
          <View style={styles.stateBox} testID={`${testID}-error`}>
            <Text style={styles.errorText}>
              Couldn’t load some device lists. Check your connection and try
              again.
            </Text>
          </View>
        ) : showInventoryEmpty ? (
          <View style={styles.emptyContainer} testID={`${testID}-empty`}>
            <View style={styles.emptyContent}>
              <View style={styles.emptyIconWrap} accessibilityElementsHidden>
                <Layers size={48} color={colors.textMuted} />
              </View>
              <Text style={styles.emptyTitle}>No devices added yet</Text>
            </View>
          </View>
        ) : (
          cards.map(card => (
            <LocationCard
              key={card.isUntagged ? 'untagged' : `tag-${card.locationTagId}`}
              card={card}
              onUnitPress={onUnitPress}
              onReorderUnits={onReorderUnits}
              canReorderStep={canReorderStep}
              resolveLocationTagName={resolveLocationTagName}
              testID={
                card.isUntagged
                  ? `${testID}-card-untagged`
                  : `${testID}-card-${card.locationTagId}`
              }
            />
          ))
        )}
      </StepShell>
    </View>
  );
};

export const DevicesView: React.FC<DevicesViewProps> = ({
  requestId,
  enabledStepIds,
  unitOverrides,
  onUnitPress,
  onReorderUnits,
  canReorderStep,
  testID,
}) => {
  const scope = useRequestScope();
  const resolvedEnabledStepIds =
    enabledStepIds ?? scope?.enabledStepIds ?? EMPTY_STEP_IDS;

  const { devicesViewModel, isLoading, isError, resolveLocationTagName } =
    useLocationViewData({
      requestId,
      enabledStepIds: resolvedEnabledStepIds,
      unitOverrides,
    });

  return (
    <DevicesViewBody
      viewModel={devicesViewModel}
      isLoading={isLoading}
      isError={isError}
      onUnitPress={onUnitPress}
      onReorderUnits={onReorderUnits}
      canReorderStep={canReorderStep}
      resolveLocationTagName={resolveLocationTagName}
      testID={testID}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    // Do not flex-grow inside the request ScrollView — that reserved a tall
    // empty band above the title on compact web/native.
    width: '100%',
    paddingBottom: spacing.lg,
  },
  stateBox: {
    paddingVertical: spacing.xl,
    alignItems: 'center',
    gap: spacing.sm,
  },
  /** Match RackConfigurationList empty state vertical centering. */
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
    minHeight: Platform.OS === 'web' ? 480 : 320,
  },
  emptyContent: {
    alignItems: 'center',
    gap: spacing.sm,
    width: '100%',
    maxWidth: 360,
  },
  emptyIconWrap: {
    marginBottom: spacing.xs,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textPrimary,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  emptyText: {
    fontSize: 13,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 13,
    color: colors.error,
    textAlign: 'center',
  },
});
